function _classPrivateFieldInitSpec(e, t, a) { _checkPrivateRedeclaration(e, t), t.set(e, a); }
function _checkPrivateRedeclaration(e, t) { if (t.has(e)) throw new TypeError("Cannot initialize the same private elements twice on an object"); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _classPrivateFieldGet(s, a) { return s.get(_assertClassBrand(s, a)); }
function _classPrivateFieldSet(s, a, r) { return s.set(_assertClassBrand(s, a), r), r; }
function _assertClassBrand(e, t, n) { if ("function" == typeof e ? e === t : e.has(t)) return arguments.length < 3 ? t : n; throw new TypeError("Private element is not present on this object"); }
import Store from "@ember-data/store";
import BioExamObservation from "core/models/loinc";
import parameterize from "core/utils/parameterize";
import trimLoincCode from "core/utils/trim-loinc-code";
export const DfgLoinc = "LOINC-62238-1";
export const RacLoinc = "LOINC-32294-1";
var _label = /*#__PURE__*/new WeakMap();
var _unit = /*#__PURE__*/new WeakMap();
export default class BioExamResult {
  constructor(result) {
    _defineProperty(this, "store", void 0);
    _defineProperty(this, "resultHash", void 0);
    _defineProperty(this, "code", void 0);
    _defineProperty(this, "value", void 0);
    _defineProperty(this, "oruStatus", void 0);
    _defineProperty(this, "isDraft", void 0);
    _defineProperty(this, "matchType", void 0);
    _defineProperty(this, "operator", void 0);
    _defineProperty(this, "selectedRawUnit", void 0);
    _classPrivateFieldInitSpec(this, _label, void 0);
    _classPrivateFieldInitSpec(this, _unit, void 0);
    this.resultHash = result;
    const {
      store,
      label,
      code,
      value,
      unit,
      oruStatus,
      isDraft,
      matchType,
      operator,
      selectedRawUnit
    } = result;
    this.code = code;
    this.value = value;
    this.oruStatus = oruStatus;
    _classPrivateFieldSet(_label, this, label);
    _classPrivateFieldSet(_unit, this, unit);
    this.isDraft = isDraft;
    this.matchType = matchType;
    this.operator = operator || "equal-to";
    this.selectedRawUnit = selectedRawUnit;
    this.store = store;
  }
  get label() {
    return this.bioExamObservation?.displayLabel || _classPrivateFieldGet(_label, this);
  }
  get unit() {
    // When the result is ignored isn't LOINC-ed, returns the raw unit
    if (this.isIgnored || this.isAdhoc) return _classPrivateFieldGet(_unit, this);

    // When the result is LOINC-ed, and the corresponding
    // observation's unit is null returns null
    if (!this.bioExamObservation?.unit) return null;

    // When the result is LOINC-ed, and the corresponding observation's
    // unit matches the raw unit, returns the observation's unit
    // OR
    // When the result is LOINC-ed, and the raw unit is null, return
    // the observation's unit
    if (!_classPrivateFieldGet(_unit, this) || _classPrivateFieldGet(_unit, this) == "" || _classPrivateFieldGet(_unit, this).toLowerCase() == this.bioExamObservation.unit?.toLowerCase()) return this.bioExamObservation.unit;

    // In any other cases returns the raw unit
    return _classPrivateFieldGet(_unit, this);
  }

  /**
   * @returns true if the result uses the "standard" unit.
   * The standard unit is the one Sêmeia uses.
   * It is usually the one from JDV Circuit Bio may differ sometimes.
   */
  get usesStandardUnit() {
    const standardUnit = this.bioExamObservation?.unit;
    if (this.bioExamObservation && !standardUnit) return true;
    return _classPrivateFieldGet(_unit, this)?.toLowerCase() == standardUnit?.toLowerCase();
  }
  get hasTextValue() {
    return this.bioExamObservation?.valueType == "string";
  }
  get hasNoValueType() {
    return !this.bioExamObservation?.valueType;
  }

  /**
   * A coherent value is either:
   * - a string value if a string is expected. This is when hasTextValue is true.
   * - a number value if a number is expected.
   *   Some results are expected to be numbers but the parsing is not perfect yet and the value may contain text, which breaks Chart.js.
   *
   * This value is used to filter the displayed values in the graph.
   */
  get hasCoherentValue() {
    if (this.isDraft) return true;
    if (this.hasTextValue) return true;
    if (this.hasNoValueType) return true;
    if (!this.bioExamObservation) return true;
    // Passed this point this.value is expected to be a number.
    // parseFloat and +(something) may return a different content if this.value contains some text, meaning this is an incoherent value.
    return parseFloat(this.value) == +this.value;
  }
  get bioExamObservation() {
    if (trimLoincCode(this.code) == "ignore") return;
    return this.store?.peekRecord("loinc", trimLoincCode(this.code));
  }
  get isAdhoc() {
    return Boolean(this.code.match(/^ADHOC/)) || !this.bioExamObservation;
  }
  get isIgnored() {
    return trimLoincCode(this.code) == "ignore";
  }

  /**
   * A duplicate has the following format:
   * "ADHOC-6690-2-DUPLICATED"
   * Meaning it's a duplicate of the 6690-2 LOINC code
   */
  get isDuplicate() {
    return this.code.startsWith("ADHOC") && this.code.endsWith("DUPLICATED");
  }
  get rawUnit() {
    return this.resultHash.originalLine?.raw?.segs?.[5] || (this.resultHash.originalLine?.raw).unit;
  }
  get matchedByExperience() {
    return this.matchType == "experience";
  }

  /**
   * Whether or not the result should be emphasized in the matching page.
   *
   * A result should be emphasized if
   *  - it has the draft status
   *  - its match type is different than `experience`, bc we consider
   *  - if its value should be a number considering the loinc matched
   *  a result matched by experience as not requiring the same attention
   *  - it's has been deduplicated by near-value
   */
  get shouldBeEmphasized() {
    if (!this.hasCoherentValue) {
      return true;
    }
    if (this.matchedByExperience) {
      return false;
    }
    if (this.isIgnored) {
      return false;
    }
    if (this.isDuplicate) {
      return false;
    }
    return this.isDraft;
  }
  get displayedValue() {
    let displayedValue;
    switch (this.operator) {
      case "greater-than":
        displayedValue = `≥ ${this.value}`;
        break;
      case "lower-than":
        displayedValue = `≤ ${this.value}`;
        break;
      default:
        displayedValue = this.value;
        break;
    }
    return displayedValue;
  }
  get isManualMatching() {
    return this.resultHash.originalLine?.raw?.segs?.[0] == "MANUAL RES";
  }
  serialize() {
    return {
      code: this.code,
      value: this.value,
      unit: this.unit,
      label: this.label,
      originalLine: this.resultHash.originalLine,
      normIndicator: this.resultHash.normIndicator,
      normValInf: this.resultHash.normValInf,
      normValSupp: this.resultHash.normValSupp,
      status: this.resultHash.status,
      oruStatus: this.oruStatus,
      isDraft: this.isDraft,
      matchType: this.matchType,
      operator: this.operator,
      selectedRawUnit: this.selectedRawUnit
    };
  }
  static generateAdhocCode(rawMatch, reason = null) {
    let adhocCode = `ADHOC-${parameterize(rawMatch)}`;
    if (reason) {
      adhocCode += `-${reason}`;
    }
    return adhocCode;
  }
  static newManualRes(result) {
    const originalLine = {
      raw: {
        segs: ["MANUAL RES", "", "", "", result.value || "", result.unit || "", "", "", "", ""]
      },
      uuid: crypto.randomUUID()
    };
    const params = Object.assign(result, {
      originalLine
    });
    return new BioExamResult(params);
  }
}